import * as THREE from 'three';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';

// Track WebGLRenderTargets created by PMREMGenerator so they can be disposed
// later — the PMREM RTs are retained because three.js samples from
// `renderTarget.texture` at runtime for env reflections.
const _envMapTargets: THREE.WebGLRenderTarget[] = [];

/** Dispose all PMREM render targets created by the env map helpers. Call on
 *  scene teardown / renderer disposal. */
export function disposeEnvMapTargets(): void {
  for (const rt of _envMapTargets) {
    rt.dispose();
  }
  _envMapTargets.length = 0;
}

// ---- Procedural Environment Map -------------------------------------------

/** Create a warm desert procedural env map using PMREMGenerator */
export function createDesertEnvMap(r: THREE.WebGLRenderer): THREE.Texture {
  const pmrem = new THREE.PMREMGenerator(r);
  pmrem.compileCubemapShader();

  // Create a simple gradient scene for the env map
  const envScene = new THREE.Scene();
  // Desert sky gradient shader
  const envMat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    uniforms: {},
    vertexShader: /* glsl */ `
      varying vec3 vWorldPos;
      void main() {
        vWorldPos = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      varying vec3 vWorldPos;
      void main() {
        vec3 dir = normalize(vWorldPos);
        float y = dir.y * 0.5 + 0.5;

        // Desert sky: warm horizon to pale blue zenith
        vec3 horizon = vec3(0.95, 0.80, 0.55);   // warm sand haze
        vec3 zenith  = vec3(0.45, 0.62, 0.88);    // deeper blue sky
        vec3 ground  = vec3(0.78, 0.65, 0.40);    // warm desert floor bounce

        vec3 color;
        if (dir.y >= 0.0) {
          color = mix(horizon, zenith, pow(y, 0.5));
          // Sun glow
          float sunDot = max(dot(dir, normalize(vec3(0.5, 0.6, 0.3))), 0.0);
          color += vec3(1.0, 0.9, 0.7) * pow(sunDot, 64.0) * 3.0;
          color += vec3(1.0, 0.85, 0.6) * pow(sunDot, 8.0) * 0.5;
        } else {
          color = mix(horizon, ground, pow(-dir.y, 0.3));
        }

        gl_FragColor = vec4(color, 1.0);
      }
    `,
  });

  const envSphere = new THREE.Mesh(new THREE.SphereGeometry(1, 32, 16), envMat);
  envScene.add(envSphere);

  const envRT = pmrem.fromScene(envScene, 0, 0.1, 100);
  envMat.dispose();
  envSphere.geometry.dispose();
  pmrem.dispose();

  // Retain the RT for later disposal — the returned texture is owned by it.
  _envMapTargets.push(envRT);
  return envRT.texture;
}

// ---- HDR Environment Map --------------------------------------------------

/** Load an HDR equirectangular env map, PMREM-process it, and return the
 *  cubemap texture. Falls back to the procedural desert env map on failure. */
export async function loadHDREnvMap(renderer: THREE.WebGLRenderer): Promise<THREE.Texture> {
  try {
    const loader = new RGBELoader();
    const hdrTexture = await loader.loadAsync('/envmaps/overcast_soil_puresky_2k.hdr');

    const pmrem = new THREE.PMREMGenerator(renderer);
    pmrem.compileCubemapShader();
    const envRT = pmrem.fromEquirectangular(hdrTexture);

    // Clean up source texture and generator
    hdrTexture.dispose();
    pmrem.dispose();

    // Retain the RT for later disposal — the returned texture is owned by it.
    _envMapTargets.push(envRT);
    return envRT.texture;
  } catch (error) {
    console.warn('Failed to load HDR env map, falling back to procedural:', error);
    return createDesertEnvMap(renderer);
  }
}
